import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/**
 * Drop-in replacement for Xano's customer-detail call, so the original screen
 * keeps its layout and only its data source changes.
 *
 * The response nests three things the screen reads separately: the customer,
 * their orders and their leads. Xano returned all three from one endpoint; the
 * new API splits them, so this fans out and stitches the shape back together.
 */

type Payload<T> = AxiosRequestConfig<T>;

/** What the migration did not carry. Shown as-is rather than guessed. */
const NOT_MIGRATED = "";

/**
 * These screens date things with getRelativeDate, which takes epoch
 * milliseconds the way Xano returned them. Handing it an ISO string renders
 * as 01-Jan-1970.
 */
const epoch = (iso: string | null | undefined): number | null =>
  iso ? new Date(iso).getTime() : null;

type CustomerDetail = {
  id: string;
  customerNo: number;
  type: string;
  lifecycleStage: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  notes: string | null;
  owner: string | null;
  createdAt?: string;
  identities: {
    docType: string;
    last4: string | null;
    verifiedAt: string | null;
  }[];
  opportunities: {
    id: string;
    leadNo: number;
    stage: string;
    product: string | null;
    expectedAmountMinor: string | null;
    currency: string | null;
    nextFollowUpAt: string | null;
    createdAt?: string;
  }[];
};

type OrderRow = {
  id: string;
  orderNo: string;
  status: string;
  product: string | null;
  placedAt: string | null;
  totalPayableMinor: string | null;
};

export type GetStaffCustomerRequestType = Partial<{
  customers_id: string | number;
  customer_id: string | number;
  headers: { Authorization: string };
}>;

/* eslint-disable @typescript-eslint/no-explicit-any */
export async function getStaffCustomer(
  payload: Payload<GetStaffCustomerRequestType> & {
    params?: { customers_id?: string | number; customer_id?: string | number };
  },
  // `any` on purpose: the screen reads deeply nested Xano relations, and a
  // narrower type here would only push the errors into the screen.
): Promise<{ data: any }> {
  const id =
    payload.data?.customers_id ??
    payload.data?.customer_id ??
    payload.params?.customers_id ??
    payload.params?.customer_id;
  if (id === undefined) throw new Error("A customer id is required.");

  const [detail, orders] = await Promise.all([
    moneefloApi.get<CustomerDetail>(`/customers/${id}`),
    moneefloApi
      .get(`/orders`, { params: { customerId: id, perPage: 50 } })
      .catch(() => ({ data: { items: [] as OrderRow[] } })),
  ]);
  const customer = detail.data;

  const doc = (type: string) => {
    const found = customer.identities.find((i) => i.docType === type);
    // Only the last four leave the database; the document itself lives in
    // object storage, which has no public address yet.
    return found?.last4 ? { url: "", last4: found.last4 } : { url: "" };
  };

  return {
    data: {
      customerDetail: {
        // The number the screen prints as "Customer ID". Links out of this
        // screen carry it, and /customers resolves either form.
        id: String(customer.customerNo),
        full_name: customer.fullName,
        email: customer.email ?? NOT_MIGRATED,
        phone_number: customer.phone ?? NOT_MIGRATED,
        address_line_1: customer.city ?? NOT_MIGRATED,
        status: statusOf(customer.lifecycleStage),
        notes: customer.notes ?? NOT_MIGRATED,
        created_at: epoch(customer.createdAt),
        // "active" is what the badge and the filter both look for; the
        // list adapter already used that word and this one did not.
        kyc_status: customer.identities.some((i) => i.verifiedAt)
          ? "active"
          : "",
        pan_soft_copy: doc("pan"),
        aadhaar_soft_copy_front: doc("aadhaar"),
        aadhaar_soft_copy_back: { url: "" },
        // Corporate and partner linkage are not modelled yet.
        companyDetail: null,
        partnerDetail: customer.owner ? { name: customer.owner } : null,
        partner_linked: Boolean(customer.owner),
        entity_table_id: null,
        contactDetails: [
          {
            contactName: customer.fullName,
            contactNumber: customer.phone ?? "",
            contactEmail: customer.email ?? "",
            isPrimary: true,
          },
        ],
      },
      orderList: ((orders.data as { items?: OrderRow[] })?.items ?? []).map(
        (order) => ({
          id: order.id,
          status: order.status,
          created_at: epoch(order.placedAt),
          createdBy: 0,
          _createdBy: null,
          _staff: null,
          _opportunity: [
            {
              _product: { name: order.product ?? "", subCategory: "" },
              assignedTo: null,
              _channel_of_interaction: [],
            },
          ],
        }),
      ),
      leadList: customer.opportunities.map((opportunity) => ({
        // The lead number, matching what getOpportunityByCompany returns. The
        // customer screen merges the two lists by id, so a uuid here shows the
        // same lead twice.
        id: String(opportunity.leadNo),
        status: opportunity.stage,
        created_at: epoch(opportunity.createdAt),
        _product: opportunity.product
          ? { name: opportunity.product, subCategory: "" }
          : null,
        product_details: [],
        _channel_of_interaction: [],
        assignedTo: null,
      })),
    },
  };
}

/**
 * The screens speak Xano's status words and the API speaks lifecycle stages.
 * One table, used in both directions, because having the list screen
 * translate and the detail screen not left the two disagreeing about the
 * same customer.
 *
 * `conditional active` and `archieved` have no lifecycle stage of their own
 * and are not offered here; see prodlist.
 */
const STAGE_TO_STATUS: Record<string, string> = {
  customer: "active",
  lead: "pending",
  prospect: "pending",
  inactive: "blocked",
};

const STATUS_TO_STAGE: Record<string, string> = {
  active: "customer",
  pending: "lead",
  blocked: "inactive",
};

/** The word the status pill keys its colour by. */
export const statusOf = (lifecycleStage: string | null | undefined): string =>
  STAGE_TO_STATUS[lifecycleStage ?? ""] ?? (lifecycleStage ?? "");

export type PutCustomerStatusRequestType = Partial<{
  customers_id: string | number;
  status: string;
  headers: { Authorization: string };
}>;

export async function putCustomerStatus(
  payload: Payload<PutCustomerStatusRequestType>,
): Promise<{ data: unknown }> {
  const id = payload.data?.customers_id;
  const status = payload.data?.status;
  if (id === undefined || !status) {
    throw new Error("A customer and a status are required.");
  }
  const stage = STATUS_TO_STAGE[status];
  if (!stage) {
    // Better a clear refusal than a 400 the screen turns into
    // "Something went wron" while the dropdown silently springs back.
    throw new Error(`"${status}" is not a status a customer can be set to`);
  }
  const { data } = await moneefloApi.patch(`/customers/${id}`, {
    lifecycleStage: stage,
  });
  return { data };
}

/* ------------------------------------------------------ the customers table */

export type GetCustomerRequestType = Partial<{
  headers: { Authorization: string };
  per_page: number;
  status: string;
  kyc_status: string;
  city: string;
}>;

type CustomerListRow = {
  id: string;
  customerNo: number;
  fullName: string;
  lifecycleStage: string;
  city: string | null;
  kycStatus: string;
  panLast4: string | null;
};

/**
 * The customers table.
 *
 * The screen has status, KYC and city dropdowns whose values it passes here,
 * but its only fetch runs on mount with an empty dependency list, so they
 * have never narrowed anything. That is how it behaved against Xano, so the
 * parameters are carried and ignored rather than the screen being changed.
 *
 * The PAN column shows the last four digits. The full number is encrypted at
 * rest and does not leave the database.
 *
 * The KYC column is empty on purpose. Xano's kyc_status was never carried
 * over -- the import brought the documents across but not whether anyone had
 * checked them, so exactly one customer of the 3,612 holding a PAN is marked
 * verified. Mapping the rest onto "not applicable", the only other word the
 * column knows, would assert that thousands of customers need no KYC. Blank
 * says what is true: we do not know. Filling it needs a fresh pull from Xano.
 */
export async function GetCustomer(
  payload: Payload<GetCustomerRequestType> & {
    params?: GetCustomerRequestType;
  },
): Promise<{ data: any }> {
  const perPage = Math.min(
    Number(payload.params?.per_page ?? payload.data?.per_page) || 100,
    100,
  );
  const { data } = await moneefloApi.get("/customers", {
    params: { perPage },
  });
  const customers = (data?.items ?? []) as CustomerListRow[];

  return {
    data: {
      items: customers.map((customer) => ({
        id: String(customer.customerNo),
        full_name: customer.fullName,
        pan_no: customer.panLast4 ? `••••${customer.panLast4}` : "",
        kyc_status: customer.kycStatus === "verified" ? "active" : "",
        address_line_1: customer.city ?? "",
        status: statusOf(customer.lifecycleStage),
      })),
    },
  };
}
